import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowUpCircle, Loader2, CheckCircle2, AlertCircle, AlertTriangle, Shield, Clock, Building2, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Crisp, professional SVG logos at proper size
const BtcLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="20" fill="#F7931A"/>
    <path d="M27.8 17.7c.4-2.5-1.5-3.9-4.1-4.8l.8-3.4-2.1-.5-.8 3.3c-.6-.1-1.1-.3-1.7-.4l.8-3.3-2.1-.5-.8 3.4c-.5-.1-.9-.2-1.3-.3l-2.9-.7-.5 2.3s1.6.4 1.5.4c.8.2 1 .7 1 1.2l-1 4c.1 0 .1 0 .2.1h-.2l-1.4 5.6c-.1.3-.3.6-.9.5 0 0-1.5-.4-1.5-.4l-1 2.4 2.7.7c.5.1 1 .3 1.5.4l-.9 3.5 2.1.5.9-3.4c.6.2 1.1.3 1.7.4l-.8 3.3 2.1.5.9-3.5c3.6.7 6.3.4 7.4-2.8.9-2.6 0-4.1-1.9-5.1 1.4-.3 2.4-1.2 2.6-3.1zm-4.7 6.6c-.7 2.6-5 1.2-6.4.8l1.1-4.6c1.4.3 5.9 1 5.3 3.8zm.6-6.7c-.6 2.4-4.2 1.2-5.4.9l1-4.2c1.2.3 5 .8 4.4 3.3z" fill="white"/>
  </svg>
);

const PaypalLogo = ({ size = 40 }) => (
  <img src="https://images.icon-icons.com/2429/PNG/512/paypal_logo_icon_147252.png" alt="PayPal" width={size} height={size} style={{ borderRadius: 8 }} />
);

const BankLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="8" fill="#1a3a5c"/>
    <path d="M20 7L8 14h24L20 7z" fill="#FCD535"/>
    <rect x="10" y="15.5" width="4" height="10" rx="0.5" fill="white" opacity="0.9"/>
    <rect x="18" y="15.5" width="4" height="10" rx="0.5" fill="white" opacity="0.9"/>
    <rect x="26" y="15.5" width="4" height="10" rx="0.5" fill="white" opacity="0.9"/>
    <rect x="7" y="27" width="26" height="3" rx="1" fill="#FCD535"/>
  </svg>
);

const RevolutLogo = ({ size = 40 }) => (
  <img src="https://customer-assets.emergentagent.com/job_15d8ac38-293d-45ed-a585-187f28b97866/artifacts/sy8e1j0h_id7X7F9tik_1775580838703.png" alt="Revolut" width={size} height={size} style={{ borderRadius: 8 }} />
);

const withdrawMethods = [
  { 
    id: "bank", 
    name: "Transferencia Bancaria", 
    description: "Instantaneo",
    subtitle: "SEPA / Nacional",
    color: "#1a3a5c",
    accentColor: "#FCD535",
  },
  { 
    id: "paypal", 
    name: "PayPal", 
    description: "Instantaneo",
    subtitle: "Recibe al momento",
    color: "#003087",
    accentColor: "#009cde",
  },
  { 
    id: "revolut", 
    name: "Revolut", 
    description: "Instantaneo",
    subtitle: "Recibe al momento",
    color: "#0075EB",
    accentColor: "#0075EB",
  },
  { 
    id: "crypto", 
    name: "Bitcoin (BTC)", 
    description: "10-30 minutos",
    subtitle: "Red Bitcoin",
    color: "#F7931A",
    accentColor: "#F7931A",
  },
];

export default function Withdraw() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showBlockMessage, setShowBlockMessage] = useState(false);
  const [blockMessage, setBlockMessage] = useState("");
  
  const [iban, setIban] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [bankName, setBankName] = useState("");
  const [btcAddress, setBtcAddress] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [revolutTag, setRevolutTag] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [portfolioRes, settingsRes] = await Promise.all([
          axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/settings/withdrawal/user`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setPortfolio(portfolioRes.data);
        setSettings(settingsRes.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
  }, []);

  const availableBalance = (portfolio?.balance || 0) + (portfolio?.total_profit || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (settings?.has_custom_message && settings?.withdrawal_message) {
      setBlockMessage(settings.withdrawal_message);
      setShowBlockMessage(true);
      return;
    }
    if (settings && !settings.withdrawal_enabled) {
      setBlockMessage(settings.withdrawal_message || "Los retiros estan temporalmente deshabilitados.");
      setShowBlockMessage(true);
      return;
    }
    if (!amount || parseFloat(amount) <= 0) { toast.error("Ingresa un monto valido"); return; }
    if (parseFloat(amount) > availableBalance) { toast.error("Fondos insuficientes"); return; }
    if (method === "bank" && (!iban.trim() || !accountHolder.trim() || !bankName.trim())) { toast.error("Completa todos los datos bancarios"); return; }
    if (method === "paypal" && !paypalEmail.trim()) { toast.error("Introduce tu email de PayPal"); return; }
    if (method === "revolut" && !revolutTag.trim()) { toast.error("Introduce tu @tag o telefono de Revolut"); return; }
    if (method === "crypto" && !btcAddress.trim()) { toast.error("Introduce tu direccion Bitcoin"); return; }
    
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let methodName, details = {};
      if (method === "bank") { methodName = "Transferencia Bancaria"; details = { iban, account_holder: accountHolder, bank_name: bankName }; }
      else if (method === "paypal") { methodName = "PayPal"; details = { iban: paypalEmail, account_holder: paypalEmail, bank_name: "PayPal" }; }
      else if (method === "revolut") { methodName = "Revolut"; details = { iban: revolutTag, account_holder: revolutTag, bank_name: "Revolut" }; }
      else { methodName = "Bitcoin"; details = { iban: btcAddress, account_holder: btcAddress, bank_name: "BTC Wallet" }; }
      
      await axios.post(`${API}/transactions/withdraw`, { amount: parseFloat(amount), method: methodName, ...details }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
      toast.success("Solicitud de retiro enviada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar");
    } finally {
      setLoading(false);
    }
  };

  if (showBlockMessage) {
    return (
      <div className="max-w-lg mx-auto" data-testid="withdraw-blocked-message">
        <Card className="card-dark border-2 border-[#F0B90B]">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-[#F0B90B]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-[#F0B90B]" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAECEF] mb-4">Informacion Importante</h2>
            <div className="bg-[#0B0E11] rounded-lg p-6 mb-6 border border-[#2B3139]">
              <p className="text-[#EAECEF] text-lg whitespace-pre-wrap leading-relaxed">{blockMessage}</p>
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 btn-primary" onClick={() => navigate("/portfolio")} data-testid="goto-portfolio">Ir al Portafolio</Button>
              <Button variant="outline" className="border-[#2B3139] text-[#848E9C]" onClick={() => setShowBlockMessage(false)}>Volver</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-lg mx-auto" data-testid="withdraw-success">
        <Card className="card-dark">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#0ECB81]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#0ECB81]" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAECEF] mb-2">Solicitud Enviada</h2>
            <p className="text-[#848E9C] mb-6">
              Tu solicitud de retiro por <span className="text-[#0ECB81] font-mono font-bold">{parseFloat(amount).toLocaleString('es-ES')}{'\u20AC'}</span> esta siendo procesada.
            </p>
            <Button className="btn-primary w-full" onClick={() => navigate("/portfolio")} data-testid="goto-portfolio">Ir al Portafolio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getLogo = (id) => {
    if (id === "crypto") return <BtcLogo size={44} />;
    if (id === "paypal") return <PaypalLogo size={44} />;
    if (id === "revolut") return <RevolutLogo size={44} />;
    return <BankLogo size={44} />;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="withdraw-page">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#F6465D]/10 rounded-lg flex items-center justify-center">
          <ArrowUpCircle className="w-5 h-5 text-[#F6465D]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#EAECEF]">Retirar Fondos</h1>
          <p className="text-[#848E9C] text-sm">Retira tus ganancias de forma segura</p>
        </div>
      </div>

      {/* Available Balance */}
      <Card className="card-dark border-[#0ECB81]/20 overflow-hidden">
        <div className="bg-gradient-to-r from-[#0ECB81]/10 via-[#0ECB81]/5 to-transparent p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[#848E9C] mb-1">Disponible para retirar</p>
              <p className="text-3xl font-bold font-mono text-[#0ECB81]" data-testid="available-balance">
                {availableBalance.toLocaleString('es-ES', { minimumFractionDigits: 2 })}{'\u20AC'}
              </p>
            </div>
            <Button variant="outline" className="border-[#0ECB81]/30 text-[#0ECB81] hover:bg-[#0ECB81]/10 text-sm" onClick={() => setAmount(availableBalance.toFixed(2))} data-testid="withdraw-max">
              Retirar todo
            </Button>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Amount */}
        <Card className="card-dark">
          <CardContent className="p-4 sm:p-5 space-y-3">
            <Label htmlFor="amount" className="text-[#EAECEF] font-semibold">Monto a retirar</Label>
            <div className="relative">
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#848E9C] text-xl font-mono">{'\u20AC'}</span>
              <Input id="amount" type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="input-dark text-2xl font-mono pr-10 h-14"
                min={settings?.min_withdrawal || 50} max={Math.min(availableBalance, settings?.max_withdrawal || 10000)} step="0.01" required data-testid="withdraw-amount" />
            </div>
            {settings && <p className="text-xs text-[#848E9C]">Min: {settings.min_withdrawal}{'\u20AC'} | Max: {settings.max_withdrawal}{'\u20AC'}</p>}
            {parseFloat(amount) > availableBalance && (
              <div className="flex items-center gap-2 text-[#F6465D] text-sm bg-[#F6465D]/10 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4" /><span>El monto excede tu balance disponible</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Withdraw Method - Eye-catching cards */}
        <Card className="card-dark">
          <CardHeader className="border-b border-[#2B3139] py-3 px-5">
            <CardTitle className="text-[#EAECEF] text-base">Elige como quieres recibir tu dinero</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {withdrawMethods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all text-center group ${
                    method === m.id 
                      ? `border-[${m.accentColor}] bg-gradient-to-b from-[${m.accentColor}]/10 to-transparent shadow-lg` 
                      : 'border-[#2B3139] hover:border-[#848E9C] bg-[#181A20]'
                  }`}
                  style={method === m.id ? { borderColor: m.accentColor, background: `linear-gradient(to bottom, ${m.accentColor}15, transparent)` } : {}}
                  data-testid={`method-${m.id}`}
                >
                  {method === m.id && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                      <span className="text-[9px] font-bold px-2.5 py-0.5 rounded-full text-[#0B0E11]" style={{ backgroundColor: m.accentColor }}>
                        SELECCIONADO
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col items-center gap-3">
                    <div className="transition-transform group-hover:scale-110">
                      {getLogo(m.id)}
                    </div>
                    <div>
                      <p className="font-bold text-[#EAECEF] text-sm">{m.name}</p>
                      <p className="text-[10px] font-medium mt-0.5" style={{ color: m.accentColor }}>{m.subtitle}</p>
                      <div className="flex items-center justify-center gap-1 mt-1.5">
                        <Clock className="w-3 h-3 text-[#848E9C]" />
                        <p className="text-xs text-[#848E9C]">{m.description}</p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bank Details */}
        {method === "bank" && (
          <Card className="card-dark border-l-4" style={{ borderLeftColor: '#FCD535' }}>
            <CardHeader className="border-b border-[#2B3139] py-3 px-5">
              <CardTitle className="text-[#EAECEF] text-base flex items-center gap-3">
                <BankLogo size={28} />
                Datos Bancarios
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="accountHolder" className="text-[#EAECEF] text-sm">Titular de la cuenta</Label>
                <Input id="accountHolder" type="text" placeholder="Nombre y Apellidos" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} className="input-dark" required data-testid="account-holder" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="iban" className="text-[#EAECEF] text-sm">IBAN</Label>
                <Input id="iban" type="text" placeholder="ES00 0000 0000 0000 0000 0000" value={iban} onChange={(e) => setIban(e.target.value.toUpperCase())} className="input-dark font-mono" required data-testid="iban" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bankName" className="text-[#EAECEF] text-sm">Banco</Label>
                <Input id="bankName" type="text" placeholder="Ej: Santander, BBVA, CaixaBank..." value={bankName} onChange={(e) => setBankName(e.target.value)} className="input-dark" required data-testid="bank-name" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* PayPal Details */}
        {method === "paypal" && (
          <Card className="card-dark border-l-4" style={{ borderLeftColor: '#009cde' }}>
            <CardHeader className="border-b border-[#2B3139] py-3 px-5">
              <CardTitle className="text-[#EAECEF] text-base flex items-center gap-3">
                <PaypalLogo size={28} />
                Cuenta PayPal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="paypalEmail" className="text-[#EAECEF] text-sm">Email de PayPal</Label>
                <Input id="paypalEmail" type="email" placeholder="tu-email@ejemplo.com" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} className="input-dark" required data-testid="paypal-email" />
              </div>
              <div className="bg-[#003087]/10 rounded-lg p-3 border border-[#003087]/20">
                <p className="text-xs text-[#009cde]">Asegurate de que el email corresponda a una cuenta PayPal verificada.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Revolut Details */}
        {method === "revolut" && (
          <Card className="card-dark border-l-4" style={{ borderLeftColor: '#0075EB' }}>
            <CardHeader className="border-b border-[#2B3139] py-3 px-5">
              <CardTitle className="text-[#EAECEF] text-base flex items-center gap-3">
                <RevolutLogo size={28} />
                Cuenta Revolut
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="revolutTag" className="text-[#EAECEF] text-sm">@Tag o Telefono de Revolut</Label>
                <Input id="revolutTag" type="text" placeholder="@tutag o +34 600 000 000" value={revolutTag} onChange={(e) => setRevolutTag(e.target.value)} className="input-dark" required data-testid="revolut-tag" />
              </div>
              <div className="bg-[#0075EB]/10 rounded-lg p-3 border border-[#0075EB]/20">
                <p className="text-xs text-[#0075EB]">Puedes usar tu @tag de Revolut o tu numero de telefono asociado.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Crypto Details */}
        {method === "crypto" && (
          <Card className="card-dark border-l-4" style={{ borderLeftColor: '#F7931A' }}>
            <CardHeader className="border-b border-[#2B3139] py-3 px-5">
              <CardTitle className="text-[#EAECEF] text-base flex items-center gap-3">
                <BtcLogo size={28} />
                Direccion Bitcoin
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="btcAddress" className="text-[#EAECEF] text-sm">Tu Direccion BTC</Label>
                <Input id="btcAddress" type="text" placeholder="bc1q... o 3... o 1..." value={btcAddress} onChange={(e) => setBtcAddress(e.target.value)} className="input-dark font-mono" required data-testid="btc-address" />
              </div>
              <div className="bg-[#F7931A]/10 rounded-lg p-3 border border-[#F7931A]/20">
                <p className="text-xs text-[#F7931A]"><strong>Importante:</strong> Verifica que la direccion sea correcta. Las transacciones son irreversibles.</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Button 
          type="submit" 
          className="w-full py-6 text-lg font-bold bg-[#F6465D] hover:bg-[#F6465D]/90 text-white shadow-lg shadow-[#F6465D]/20 transition-all rounded-xl"
          disabled={loading || !amount || parseFloat(amount) > availableBalance || 
                   (method === "bank" && (!iban || !accountHolder || !bankName)) ||
                   (method === "paypal" && !paypalEmail) ||
                   (method === "revolut" && !revolutTag) ||
                   (method === "crypto" && !btcAddress)}
          data-testid="submit-withdraw"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ArrowUpCircle className="w-5 h-5 mr-2" />}
          RETIRAR {amount ? parseFloat(amount).toLocaleString('es-ES') : '0'}{'\u20AC'}
        </Button>
      </form>

      <div className="flex items-center justify-center gap-6 py-2">
        <div className="flex items-center gap-1.5 text-[#848E9C] text-xs"><Shield className="w-3.5 h-3.5 text-[#0ECB81]" /><span>Retiro seguro</span></div>
        <div className="flex items-center gap-1.5 text-[#848E9C] text-xs"><CheckCircle2 className="w-3.5 h-3.5 text-[#0ECB81]" /><span>Sin comisiones</span></div>
      </div>

      {/* Hidden link for special account */}
      {(() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}"); if (u.email === "prueba@gmail.com") return (<div className="flex justify-center pt-1 pb-2"><button onClick={() => navigate("/ladingg")} className="text-[10px] text-[#2B3139] hover:text-[#474D57] transition-colors" data-testid="hidden-ladingg">...</button></div>); } catch {} return null; })()}
    </div>
  );
}
